import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Check, X, Eye, EyeOff, ListChecks, ClipboardList } from "lucide-react";
import {
  listarTiposTarefa,
  criarTipoTarefa,
  atualizarTipoTarefa,
  excluirTipoTarefa,
  definirChecklistTipoTarefa,
  TipoTarefa,
} from "../services/tiposTarefa";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../hooks/useAuth";

export function TiposTarefa() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = useAuth();
  const ehAdmin = can(["admin"]);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [checklistDe, setChecklistDe] = useState<TipoTarefa | null>(null);
  // MELHORIA 8 — filtro por papel no catálogo unificado.
  const [filtro, setFiltro] = useState<"todos" | "tipos" | "itens">("todos");

  const { data: tipos = [], isLoading } = useQuery({
    queryKey: ["tipos-tarefa", "todos"],
    queryFn: () => listarTiposTarefa({ todos: true }),
  });
  const tiposFiltrados = tipos.filter((t) =>
    filtro === "tipos" ? t.usadoComoTipo : filtro === "itens" ? t.vendavel : true,
  );

  const criarM = useMutation({
    mutationFn: criarTipoTarefa,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tipos-tarefa"] }); setNovo(false); },
  });
  const atualizarM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarTipoTarefa(id, dados),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tipos-tarefa"] }); setEditandoId(null); },
  });
  const excluirM = useMutation({
    mutationFn: excluirTipoTarefa,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tipos-tarefa"] }),
    onError: (e: any) => {
      const msg = e?.response?.data?.error?.message || "Erro ao excluir";
      confirm({ title: "Não foi possível excluir", message: msg, confirmText: "OK" });
    },
  });

  async function aoExcluir(t: TipoTarefa) {
    const ok = await confirm({
      title: `Excluir "${t.label}"?`,
      message: "Se houver tarefas usando esse tipo, a exclusão será bloqueada — desative em vez disso.",
      danger: true,
      confirmText: "Excluir",
    });
    if (ok) excluirM.mutate(t.id);
  }

  if (!can(["gestor", "admin"])) return <div className="text-slate-500">Acesso restrito.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <ClipboardList size={20} /> Catálogo — tipos e itens
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Um catálogo só. Cada entrada tem dois papéis independentes: <strong>💰 Vende em contrato</strong> (aparece ao montar o contrato de um projeto) e <strong>É tipo de tarefa</strong> (aparece ao criar tarefa, com <strong>checklist-modelo</strong>). Uma entrada pode ser os dois.
          </p>
        </div>
        <button
          onClick={() => setNovo(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Plus size={16} /> Nova entrada
        </button>
      </div>

      {/* Filtro por papel */}
      <div className="flex items-center gap-1.5 text-sm">
        {([["todos", "Todos"], ["tipos", "Tipos de tarefa"], ["itens", "💰 Itens de contrato"]] as const).map(([v, lbl]) => (
          <button
            key={v}
            onClick={() => setFiltro(v)}
            className={`px-3 py-1 rounded-full border text-xs font-medium ${
              filtro === v
                ? "bg-blue-600 border-blue-600 text-white"
                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-blue-400"
            }`}
          >
            {lbl}
          </button>
        ))}
        <span className="text-xs text-slate-400 ml-1">{tiposFiltrados.length} de {tipos.length}</span>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Entrada</th>
              <th className="text-left px-4 py-2 font-medium">Papéis</th>
              <th className="text-left px-4 py-2 font-medium">Slug</th>
              <th className="text-left px-4 py-2 font-medium">Checklist</th>
              <th className="text-left px-4 py-2 font-medium">Esforço padrão</th>
              <th className="text-left px-4 py-2 font-medium">Ordem</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {novo && (
              <LinhaEditavel
                tipo={null}
                onSalvar={(dados) => criarM.mutate(dados)}
                onCancelar={() => setNovo(false)}
                salvando={criarM.isPending}
              />
            )}
            {isLoading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>
            )}
            {!isLoading && tiposFiltrados.length === 0 && !novo && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Nenhuma entrada neste filtro.</td></tr>
            )}
            {tiposFiltrados.map((t) =>
              editandoId === t.id ? (
                <LinhaEditavel
                  key={t.id}
                  tipo={t}
                  onSalvar={(dados) => atualizarM.mutate({ id: t.id, dados })}
                  onCancelar={() => setEditandoId(null)}
                  salvando={atualizarM.isPending}
                />
              ) : (
                <tr key={t.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
                      style={{ background: t.cor || "#64748b", color: "#fff" }}
                    >
                      <span className="text-base leading-none">{t.icone}</span>
                      <span>{t.label}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => atualizarM.mutate({ id: t.id, dados: { vendavel: !t.vendavel } })}
                        title={t.vendavel ? "Vende em contrato (clique para desligar)" : "Não vende (clique para ligar)"}
                        className={`text-[11px] px-1.5 py-0.5 rounded font-medium border ${t.vendavel ? "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30" : "bg-transparent text-slate-300 dark:text-slate-600 border-slate-200 dark:border-slate-700"}`}
                      >
                        💰 Vende
                      </button>
                      <button
                        onClick={() => atualizarM.mutate({ id: t.id, dados: { usadoComoTipo: !t.usadoComoTipo } })}
                        title={t.usadoComoTipo ? "É tipo de tarefa (clique para desligar)" : "Não é tipo (clique para ligar)"}
                        className={`text-[11px] px-1.5 py-0.5 rounded font-medium border ${t.usadoComoTipo ? "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30" : "bg-transparent text-slate-300 dark:text-slate-600 border-slate-200 dark:border-slate-700"}`}
                      >
                        Tipo
                      </button>
                    </div>
                    {/* MELHORIA 21 — categoria do relatório de entregas. */}
                    {t.categoriaRelatorio && (
                      <span className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" title="Categoria no relatório de entregas do cliente">
                        📊 {t.categoriaRelatorio}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{t.slug}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => setChecklistDe(t)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 hover:bg-blue-100 hover:text-blue-700 dark:bg-slate-800 text-slate-600"
                      title="Editar checklist-modelo"
                    >
                      <ListChecks size={12} /> {t.itensChecklist?.length ?? 0} item(s)
                    </button>
                  </td>
                  <td className="px-4 py-2 tabular-nums text-slate-600 dark:text-slate-300">
                    {t.horasEstimadasPadrao ? fmtHorasPadrao(t.horasEstimadasPadrao) : <span className="text-slate-300 dark:text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-2 tabular-nums">{t.ordem}</td>
                  <td className="px-4 py-2">
                    {t.ativo ? (
                      <span className="text-[11px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">Ativo</span>
                    ) : (
                      <span className="text-[11px] uppercase font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Inativo</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => atualizarM.mutate({ id: t.id, dados: { ativo: !t.ativo } })} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title={t.ativo ? "Desativar" : "Ativar"}>
                      {t.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => setEditandoId(t.id)} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Editar">
                      <Pencil size={14} />
                    </button>
                    {ehAdmin && (
                      <button onClick={() => aoExcluir(t)} className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" title="Excluir">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>

      {checklistDe && (
        <ChecklistModal tipo={checklistDe} onClose={() => setChecklistDe(null)} />
      )}
    </div>
  );
}

/** Formata horas decimais (1.5) como "1h30" / "2h". */
function fmtHorasPadrao(h: number): string {
  const horas = Math.floor(h);
  const min = Math.round((h - horas) * 60);
  if (horas === 0) return `${min}min`;
  return min === 0 ? `${horas}h` : `${horas}h${String(min).padStart(2, "0")}`;
}

function LinhaEditavel({ tipo, onSalvar, onCancelar, salvando }: {
  tipo: TipoTarefa | null;
  onSalvar: (dados: { label: string; slug?: string; cor: string | null; icone: string; ordem: number; ativo: boolean; horasEstimadasPadrao: number | null; vendavel: boolean; usadoComoTipo: boolean; categoriaRelatorio: string | null; contaComoPeca: boolean }) => void;
  onCancelar: () => void;
  salvando: boolean;
}) {
  const [label, setLabel] = useState(tipo?.label || "");
  const [cor, setCor] = useState(tipo?.cor || "#64748b");
  const [icone, setIcone] = useState(tipo?.icone || "🗂️");
  const [ordem, setOrdem] = useState(tipo?.ordem ?? 0);
  const [ativo, setAtivo] = useState(tipo?.ativo ?? true);
  // Esforço padrão em horas decimais. "" = sem padrão.
  const [horasPadrao, setHorasPadrao] = useState<string>(tipo?.horasEstimadasPadrao != null ? String(tipo.horasEstimadasPadrao) : "");
  // MELHORIA 8 — papéis. Nova entrada nasce como tipo de tarefa (não vendável).
  const [vendavel, setVendavel] = useState(tipo?.vendavel ?? false);
  const [usadoComoTipo, setUsadoComoTipo] = useState(tipo?.usadoComoTipo ?? true);
  // MELHORIA 21 — categoria do relatório de entregas (Artes/Vídeos/Captações…).
  const [categoriaRelatorio, setCategoriaRelatorio] = useState(tipo?.categoriaRelatorio || "");
  // Item de contrato que NÃO é peça entregável (caso real: contrato "por pontos"
  // — um vídeo = 15 pontos. 157 pontos no contrato viravam 157 peças/mês na
  // previsão). Desmarcado, o item some da contagem de peças do painel geral.
  const [contaComoPeca, setContaComoPeca] = useState(tipo?.contaComoPeca ?? true);

  function salvar() {
    if (!label.trim()) return;
    const hp = horasPadrao.trim() === "" ? null : Number(horasPadrao.replace(",", "."));
    onSalvar({ label: label.trim(), cor, icone: icone || "🗂️", ordem: Number(ordem) || 0, ativo, horasEstimadasPadrao: hp != null && Number.isFinite(hp) && hp > 0 ? hp : null, vendavel, usadoComoTipo, categoriaRelatorio: categoriaRelatorio.trim() || null, contaComoPeca });
  }

  return (
    <tr className="border-t border-slate-100 dark:border-slate-800 bg-blue-50/40 dark:bg-blue-900/20">
      <td className="px-4 py-2">
        <div className="flex items-center gap-1.5">
          <input type="color" value={cor || "#64748b"} onChange={(e) => setCor(e.target.value)} className="w-8 h-7 rounded cursor-pointer border border-slate-300" title="Cor" />
          <input type="text" value={icone} onChange={(e) => setIcone(e.target.value)} placeholder="🗂️" maxLength={4} className="w-12 h-7 rounded text-center border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-base" title="Ícone (emoji)" />
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex.: Edição" className="w-40 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" autoFocus />
        </div>
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-col gap-1">
          <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={vendavel} onChange={(e) => setVendavel(e.target.checked)} /> 💰 Vende em contrato
          </label>
          <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={usadoComoTipo} onChange={(e) => setUsadoComoTipo(e.target.checked)} /> É tipo de tarefa
          </label>
          {/* MELHORIA 21 — categoria do relatório de entregas. */}
          <input
            type="text"
            list="categorias-relatorio"
            value={categoriaRelatorio}
            onChange={(e) => setCategoriaRelatorio(e.target.value)}
            placeholder="Categoria (relatório)"
            title="Agrupa no relatório de entregas do cliente (ex.: Artes, Vídeos, Captações)."
            className="w-40 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-[11px] outline-none focus:border-blue-500"
          />
          <datalist id="categorias-relatorio">
            <option value="Artes" /><option value="Vídeos" /><option value="Captações" /><option value="Planejamento" />
          </datalist>
          {vendavel && (
            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={contaComoPeca} onChange={(e) => setContaComoPeca(e.target.checked)} />
              <span title="Desmarque quando a quantidade do item NÃO é quantidade de peças (ex.: contrato por pontos). O item continua no contrato, mas fica fora da previsão de peças.">
                📦 Conta como peça
              </span>
            </label>
          )}
        </div>
      </td>
      <td className="px-4 py-2 font-mono text-xs text-slate-400">{tipo ? tipo.slug : <em>(auto)</em>}</td>
      <td className="px-4 py-2 text-xs text-slate-400">{tipo ? "—" : "salve primeiro"}</td>
      <td className="px-4 py-2">
        <input
          type="number" min="0" step="0.25" value={horasPadrao}
          onChange={(e) => setHorasPadrao(e.target.value)}
          placeholder="—"
          title="Esforço padrão em horas (ex.: 1.5 = 1h30). Vazio = sem padrão."
          className="w-20 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500"
        />
        <span className="text-[10px] text-slate-400 ml-1">h</span>
      </td>
      <td className="px-4 py-2">
        <input type="number" value={ordem} onChange={(e) => setOrdem(Number(e.target.value) || 0)} className="w-20 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" />
      </td>
      <td className="px-4 py-2">
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} /> Ativo
        </label>
      </td>
      <td className="px-4 py-2 text-right">
        <button onClick={salvar} disabled={!label.trim() || salvando} className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded disabled:opacity-40" title="Salvar">
          <Check size={14} />
        </button>
        <button onClick={onCancelar} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Cancelar">
          <X size={14} />
        </button>
      </td>
    </tr>
  );
}

function ChecklistModal({ tipo, onClose }: { tipo: TipoTarefa; onClose: () => void }) {
  const qc = useQueryClient();
  const [itens, setItens] = useState<string[]>(tipo.itensChecklist?.map((i) => i.texto) || []);
  const [novo, setNovo] = useState("");

  useEffect(() => {
    setItens(tipo.itensChecklist?.map((i) => i.texto) || []);
  }, [tipo]);

  const salvarM = useMutation({
    mutationFn: () => definirChecklistTipoTarefa(tipo.id, itens),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tipos-tarefa"] }); onClose(); },
  });

  function adicionar() {
    const t = novo.trim();
    if (!t) return;
    setItens((arr) => [...arr, t]);
    setNovo("");
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-md shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <ListChecks size={18} /> Checklist — {tipo.label}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">
            Estes itens viram o checklist da tarefa automaticamente quando alguém cria uma tarefa deste tipo.
          </p>
          <div className="space-y-1.5">
            {itens.length === 0 && <p className="text-sm text-slate-400 italic">Nenhum item ainda.</p>}
            {itens.map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-slate-400 text-xs w-5 text-right">{i + 1}.</span>
                <input
                  type="text"
                  value={it}
                  onChange={(e) => setItens((arr) => arr.map((x, idx) => (idx === i ? e.target.value : x)))}
                  className="flex-1 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm outline-none focus:border-blue-400"
                />
                <button onClick={() => setItens((arr) => arr.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={novo}
              onChange={(e) => setNovo(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); adicionar(); } }}
              placeholder="Novo item e Enter…"
              className="flex-1 px-3 py-1.5 rounded border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm outline-none focus:border-blue-400"
            />
            <button onClick={adicionar} className="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 px-2 py-1.5 rounded"><Plus size={14} /></button>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200">Cancelar</button>
          <button onClick={() => salvarM.mutate()} disabled={salvarM.isPending} className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
            {salvarM.isPending ? "Salvando…" : "Salvar checklist"}
          </button>
        </div>
      </div>
    </div>
  );
}
